/**
 * Dayjs Locale Utility
 * Maps application language codes to dayjs locales and keeps the global dayjs
 * locale in sync with the active i18n language, so month/weekday names in the
 * charts follow the user's language instead of always rendering in English.
 *
 * Only the nine supported locales are imported, to keep the bundle small.
 */

import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/da";
import "dayjs/locale/nl";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/nb";
import "dayjs/locale/th";
import "dayjs/locale/zh-cn";

dayjs.extend(localizedFormat);

/**
 * Maps application language codes to dayjs locale names
 */
const dayjsLocaleMap = {
	en: "en",
	de: "de",
	ger: "de", // Backward compatibility
	da: "da",
	dk: "da", // Backward compatibility
	nl: "nl",
	fr: "fr",
	es: "es",
	thai: "th",
	zh: "zh-cn",
	no: "nb",
};

/**
 * Gets the dayjs locale name for a given app language code.
 * @param {string} [language] - Application language code (e.g., "de", "thai")
 * @returns {string} dayjs locale name, defaults to "en"
 */
export function getDayjsLocale(language) {
	if (!language) return "en";
	return dayjsLocaleMap[String(language).toLowerCase().trim()] || "en";
}

/**
 * Applies the dayjs locale that matches an app language code.
 * @param {string} language - Application language code
 * @returns {string} The dayjs locale that was applied
 */
export function applyDayjsLocale(language) {
	const locale = getDayjsLocale(language);
	dayjs.locale(locale);
	return locale;
}

/**
 * Binds the global dayjs locale to an i18next instance: applies the current
 * language immediately and follows every `languageChanged` event afterwards.
 * @param {import("i18next").i18n} i18nInstance - i18next instance
 * @returns {void}
 */
export function bindDayjsLocale(i18nInstance) {
	if (!i18nInstance) return;
	applyDayjsLocale(i18nInstance.language);
	i18nInstance.on("languageChanged", (language) => applyDayjsLocale(language));
}
